import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import CustomGS3DViewer from "core/three/CustomGS3DViewer";
import Three from "core/three/Three";
import { toCssBackground } from "utils/background";

// 點雲預設白底（改版前就是這樣）
const DEFAULT_CLEAR_COLOR = 0xffffff;

// 背景圖時畫布要透明，才透得出底下的 CSS 背景。
// core/three/Three 的 renderer 沒開 alpha，所以只有背景圖這個情況自己建 renderer；
// 預設與純色仍走原本的 Three，畫法與改版前完全相同
const createRenderer = (background, container) => {
  if (background?.type === "image") {
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    return renderer;
  }
  const three = new Three();
  three.init(container);
  return three.renderer;
};

const applyClearColor = (renderer, background) => {
  if (!renderer || background?.type === "image") return;
  renderer.setClearColor(background?.type === "color" ? background.color : DEFAULT_CLEAR_COLOR);
};

const ThreeJs = React.memo(({ setloadingStatus, currentModel, background }) => {
  const viewerRef = useRef(null);
  const containerRef = useRef(null);
  const initializedRef = useRef(false);

  const onWindowResize = () => {
    if (viewerRef.current && containerRef.current) {
      const { clientWidth: width, clientHeight: height } = containerRef.current;
      const viewer = viewerRef.current;
      viewer.renderer.setSize(width, height);
      viewer.camera.aspect = width / height;
      viewer.camera.updateProjectionMatrix();
    }
  };

  useEffect(() => {
    if (!initializedRef.current) {
      const renderer = createRenderer(background, containerRef.current);
      applyClearColor(renderer, background);

      const viewer = new CustomGS3DViewer({
        renderer,
        cameraUp: [0, 0, 1],
        initialCameraLookAt: [0, 0, 0],
        sharedMemoryForWorkers: false,
      });

      viewerRef.current = viewer;

      viewer
        .addSplatScene(currentModel.splatSrc, {
          splatAlphaRemovalThreshold: 5,
          showLoadingUI: true,
          scale: [5, 5, 5],
        })
        .then(() => {
          viewer.start();
          setloadingStatus(false);
        });

      window.addEventListener("resize", onWindowResize);
      initializedRef.current = true;
    }
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  // 純色／預設之間切換時更新清除色（背景圖是建立 renderer 時決定的透明畫布）
  useEffect(() => {
    applyClearColor(viewerRef.current?.renderer, background);
  }, [background]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        background: background?.type === "image" ? toCssBackground(background) : undefined,
      }}
    />
  );
});

export default ThreeJs;
